import React, { useState, useEffect } from 'react';
import crossPink from '../../../images/crossPink.png';
import fileIcon from '../../../images/redAlert.png';
import yellowAlert from '../../../images/yellowAlert.png';
import greenCheck from "../../../images/checkImage.png";
import yellowWarning from '../../../images/yellowWarning.png';
import CompletedIcon from '../../../images/checkImage.png';
import ContractHeader from '../../../Components/ContractHeader';
import styles from './DeleteContractModal.module.scss';
import { Dialog, Classes, Icon, Intent, } from '@blueprintjs/core';
import { format } from "date-fns";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { TenantID, DELETE, GET } from '../../dataSaver';
import { Auth } from '../../../utils/auth';
import { config } from '../../../config';
import { SuccessToaster } from '../../../utils/toaster';

const DeleteContractModal = ({ isOpen, onClose, contractId, contractData }) => {
    const [deleteReason, setDeleteReason] = useState('');
    const [deleteOption, setDeleteOption] = useState('deleteDraft');
    const [isDeleting, setIsDeleting] = useState(false);
    const [apiContractData, setApiContractData] = useState(null);
    const [workflowData, setWorkflowData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    console.log('DeleteContractModal received contractData:', contractData);
    console.log('DeleteContractModal received contractId:', contractId);

    const fetchWorkflowData = async (apiContractId) => {
        try {
            console.log('Fetching workflow data for contractId:', apiContractId);
            const response = await GET(`timesheet-management-service/contractWorkflow/${apiContractId}`);
            console.log('Workflow API response:', response.data);
            setWorkflowData(response.data);
        } catch (error) {
            console.error('Error fetching workflow data:', error);
            setWorkflowData(null);
        }
    };

    const fetchContractData = async () => {
        if (!contractId) {
            console.log('No contractId provided, skipping API call');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const apiUrl = `contract-managment-service/contracts/${contractId}`;
            console.log('Fetching contract data with API URL:', apiUrl);

            const [contractResponse] = await Promise.all([
                GET(apiUrl),
                fetchWorkflowData(contractId)
            ]);

            console.log('Contract API response:', contractResponse);

            if (contractResponse.data) {
                setApiContractData(contractResponse.data);
                console.log('Contract data fetched successfully:', contractResponse.data);

                const contractData = contractResponse.data.contract || contractResponse.data;
                if (contractData.timesheetWorkflows || contractData.invoiceWorkflows) {
                    setWorkflowData({
                        timesheetWorkflows: contractData.timesheetWorkflows || [],
                        invoiceWorkflows: contractData.invoiceWorkflows || []
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching contract data:', error);
            setError('Failed to fetch contract data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && contractId) {
            fetchContractData();
        }
    }, [isOpen, contractId]);

    const getWorkflowSectionStatus = (sectionTitle, workflowData) => {
        if (!workflowData) {
            return "inactive";
        }

        switch (sectionTitle) {
            case "Activity Logging":
                if (workflowData.timesheetWorkflows && workflowData.timesheetWorkflows.length > 0) {
                    const hasIncompleteActivityLogging = workflowData.timesheetWorkflows.some(workflow => {
                        const activityLogDetail = workflow.activityLoggerDetail;
                        return !activityLogDetail || activityLogDetail.completionStatus !== "COMPLETED";
                    });

                    const hasPartialActivityLogging = workflowData.timesheetWorkflows.some(workflow => {
                        const activityLogDetail = workflow.activityLoggerDetail;
                        return activityLogDetail && activityLogDetail.completionStatus === "PARTIAL";
                    });

                    if (hasIncompleteActivityLogging) {
                        return hasPartialActivityLogging ? "partial" : "not_completed";
                    }
                    return "completed";
                }
                return "inactive";

            case "Timesheet Creation & Submission":
                if (workflowData.timesheetWorkflows && workflowData.timesheetWorkflows.length > 0) {
                    const hasSubmissionUsers = workflowData.timesheetWorkflows.some(workflow =>
                        workflow.timesheetSubmissionUser !== null
                    );

                    if (!hasSubmissionUsers) {
                        return "not_completed";
                    }

                    const hasIncompleteSubmission = workflowData.timesheetWorkflows.some(workflow => {
                        const submissionUser = workflow.timesheetSubmissionUser;
                        return submissionUser && submissionUser.completionStatus !== "COMPLETED";
                    });

                    const hasPartialSubmission = workflowData.timesheetWorkflows.some(workflow => {
                        const submissionUser = workflow.timesheetSubmissionUser;
                        return submissionUser && submissionUser.completionStatus === "PARTIAL";
                    });

                    if (hasIncompleteSubmission) {
                        return hasPartialSubmission ? "partial" : "not_completed";
                    }
                    return "completed";
                }
                return "inactive";

            case "Timesheet Review & Approval":
                if (workflowData.timesheetWorkflows && workflowData.timesheetWorkflows.length > 0) {
                    const hasWorkflowConfig = workflowData.timesheetWorkflows.some(workflow =>
                        workflow.timesheetWorkflowConfiguration !== null
                    );

                    if (!hasWorkflowConfig) {
                        return "not_completed";
                    }

                    const hasIncompleteWorkflow = workflowData.timesheetWorkflows.some(workflow => {
                        const config = workflow.timesheetWorkflowConfiguration;
                        if (!config) return true;

                        if (config.completionStatus === "NOT_COMPLETED") return true;
                        if (!config.hasTimesheetApproval) return true;
                        if (!config.authorizedUserIds || config.authorizedUserIds.length === 0) return true;

                        return false;
                    });

                    const hasPartialWorkflow = workflowData.timesheetWorkflows.some(workflow => {
                        const config = workflow.timesheetWorkflowConfiguration;
                        return config && config.completionStatus === "PARTIAL";
                    });

                    if (hasIncompleteWorkflow) {
                        return hasPartialWorkflow ? "partial" : "not_completed";
                    }
                    return "completed";
                }
                return "inactive";

            case "Invoice Review & Approval":
                if (workflowData.invoiceWorkflows && workflowData.invoiceWorkflows.length > 0) {
                    const hasIncompleteInvoice = workflowData.invoiceWorkflows.some(invoiceWorkflow => {
                        const completionStatus = invoiceWorkflow.invoiceWorkflowConfiguration?.completionStatus ||
                            invoiceWorkflow.completionStatus;
                        return !completionStatus || completionStatus !== "COMPLETED";
                    });

                    const hasPartialInvoice = workflowData.invoiceWorkflows.some(invoiceWorkflow => {
                        const completionStatus = invoiceWorkflow.invoiceWorkflowConfiguration?.completionStatus ||
                            invoiceWorkflow.completionStatus;
                        return completionStatus === "PARTIAL";
                    });

                    if (hasIncompleteInvoice) {
                        return hasPartialInvoice ? "partial" : "not_completed";
                    }
                    return "completed";
                }
                return "inactive";

            default:
                return "inactive";
        }
    };

    const getAlertsAndIcons = () => {
        const alerts = [];
        const alertIcons = [];

        if (!apiContractData?.contract) {
            alerts.push("Contract Data Required");
            alerts.push("Workflow Configuration Incomplete");
            alertIcons.push(fileIcon);
            alertIcons.push(yellowAlert);
            return { alerts, alertIcons };
        }

        const contract = apiContractData.contract;
        let hasDocuments = false;
        if (contract.contractDetail?.contractFiles && contract.contractDetail.contractFiles.length > 0) {
            const validDocuments = contract.contractDetail.contractFiles.filter(file =>
                file && file.fileURL && file.fileURL.trim() !== ''
            );
            hasDocuments = validDocuments.length > 0;
        }

        const contractSections = [
            {
                title: "Attached Contract Document(s)",
                status: hasDocuments ? "completed" : "not_completed"
            },
            {
                title: "Contract Identification & Term Limit",
                status: (() => {
                    if (contract.contractDetail) {
                        return "completed";
                    }
                    if (!contract.contractDetail) {
                        return "inactive";
                    }
                    return "inactive";
                })()
            },
            {
                title: "Contracted Services Provider(s)",
                status: (() => {
                    if (contract.contractServiceProviders?.length > 0) {
                        const hasIncompleteProviders = contract.contractServiceProviders.some(provider => {
                            const hasBasicInfo = provider.name?.firstName &&
                                provider.name?.lastName &&
                                provider.contractedServiceProviderType?.id &&
                                provider.sites?.sites?.length > 0;

                            return !hasBasicInfo;
                        });

                        if (hasIncompleteProviders) {
                            return "not_completed";
                        } else {
                            return "completed";
                        }
                    } else {
                        return "completed";
                    }
                })()
            },
            {
                title: "Contractor Business Entity",
                status: (() => {
                    if (!contract.contractorBusinessEntity || contract.contractorBusinessEntity.id === null) {
                        return "inactive";
                    }

                    if (contract.contractorBusinessEntity.completionStatus === "NOT_COMPLETED") {
                        return "not_completed";
                    }
                    if (contract.contractorBusinessEntity.completionStatus === "PARTIAL") {
                        return "partial";
                    }
                    if (contract.contractorBusinessEntity.completionStatus === "COMPLETED") {
                        return "completed";
                    }

                    return "inactive";
                })()
            },
            {
                title: "Contracted Services Specification",
                status: (() => {
                    if (!contract.contractedServices || contract.contractedServices.length === 0) {
                        return "inactive";
                    }

                    const hasNotCompletedServices = contract.contractedServices.some(service =>
                        service.completionStatus === "NOT_COMPLETED"
                    );
                    const hasPartialServices = contract.contractedServices.some(service =>
                        service.completionStatus === "PARTIAL"
                    );
                    const hasCompletedServices = contract.contractedServices.some(service =>
                        service.completionStatus === "COMPLETED"
                    );

                    if (hasNotCompletedServices) {
                        return "not_completed";
                    }
                    if (hasPartialServices) {
                        return "partial";
                    }
                    if (hasCompletedServices) {
                        return "completed";
                    }

                    return "inactive";
                })()
            },
            {
                title: "Timesheet Submission Terms",
                status: (() => {
                    const timesheetTermsArray = Array.isArray(contract.timesheetSubmissionTerms) ?
                        contract.timesheetSubmissionTerms :
                        (contract.timesheetSubmissionTerms ? [contract.timesheetSubmissionTerms] : []);

                    if (timesheetTermsArray.length === 0) {
                        return "inactive";
                    }

                    const hasNotCompletedTerms = timesheetTermsArray.some(terms =>
                        terms.completionStatus === "NOT_COMPLETED"
                    );
                    const hasPartialTerms = timesheetTermsArray.some(terms =>
                        terms.completionStatus === "PARTIAL"
                    );
                    const hasCompletedTerms = timesheetTermsArray.some(terms =>
                        terms.completionStatus === "COMPLETED"
                    );

                    if (hasNotCompletedTerms) {
                        return "not_completed";
                    }
                    if (hasPartialTerms) {
                        return "partial";
                    }
                    if (hasCompletedTerms) {
                        return "completed";
                    }

                    return "inactive";
                })()
            },
            {
                title: "Payment & Compensation",
                status: (() => {
                    if (!contract.paymentAndCompensation) {
                        return "inactive";
                    }
                    if (contract.paymentAndCompensation.completionStatus === "NOT_COMPLETED") {
                        return "not_completed";
                    }
                    if (contract.paymentAndCompensation.completionStatus === "PARTIAL") {
                        return "partial";
                    }
                    if (contract.paymentAndCompensation.completionStatus === "COMPLETED") {
                        return "completed";
                    }

                    return "inactive";
                })()
            }
        ];

        const workflowSections = [
            {
                title: "Activity Logging",
                status: getWorkflowSectionStatus("Activity Logging", workflowData)
            },
            {
                title: "Timesheet Creation & Submission",
                status: getWorkflowSectionStatus("Timesheet Creation & Submission", workflowData)
            },
            {
                title: "Timesheet Review & Approval",
                status: getWorkflowSectionStatus("Timesheet Review & Approval", workflowData)
            },
            {
                title: "Invoice Review & Approval",
                status: getWorkflowSectionStatus("Invoice Review & Approval", workflowData)
            }
        ];

        const sectionsWithWarnings = contractSections.filter(section => section.status === 'not_completed');
        const sectionsWithPartials = contractSections.filter(section => section.status === 'partial');
        const allContractSectionsCompleted = contractSections.every(section => section.status === 'completed');

        if (sectionsWithWarnings.length > 0) {
            alerts.push('Contract Data Required');
            alertIcons.push(fileIcon);
        } else if (sectionsWithPartials.length > 0) {
            alerts.push('Contract Data Partially Completed');
            alertIcons.push(yellowAlert);
        } else if (allContractSectionsCompleted) {
            alerts.push('Contract Data Completed');
            alertIcons.push(greenCheck);
        } else {
            alerts.push('Contract Data Required');
            alertIcons.push(fileIcon);
        }

        const incompleteWorkflows = workflowSections.filter(section =>
            section.status === 'inactive' || section.status === 'not_completed'
        );
        const partialWorkflows = workflowSections.filter(section => section.status === 'partial');
        const allWorkflowsCompleted = workflowSections.every(section => section.status === 'completed');

        if (incompleteWorkflows.length > 0) {
            alerts.push('Workflow Configuration Incomplete');
            alertIcons.push(fileIcon);
        } else if (partialWorkflows.length > 0) {
            alerts.push('Workflow Configuration Partially Completed');
            alertIcons.push(yellowAlert);
        } else if (allWorkflowsCompleted) {
            alerts.push('Workflow Configuration Completed');
            alertIcons.push(greenCheck);
        } else {
            alerts.push('Workflow Configuration Incomplete');
            alertIcons.push(fileIcon);
        }

        return { alerts, alertIcons };
    };

    const getAlertIcon = () => {
        if (!apiContractData?.contract) {
            return fileIcon;
        }

        const contract = apiContractData.contract;
        let hasDocuments = false;
        if (contract.contractDetail?.contractFiles && contract.contractDetail.contractFiles.length > 0) {
            const validDocuments = contract.contractDetail.contractFiles.filter(file =>
                file && file.fileURL && file.fileURL.trim() !== ''
            );
            hasDocuments = validDocuments.length > 0;
        }

        const contractSections = [
            {
                title: "Attached Contract Document(s)",
                status: hasDocuments ? "completed" : "not_completed"
            },
            {
                title: "Contract Identification & Term Limit",
                status: (() => {
                    if (contract.contractDetail) {
                        return "completed";
                    }
                    if (!contract.contractDetail) {
                        return "inactive";
                    }
                    return "inactive";
                })()
            },
            {
                title: "Contracted Services Provider(s)",
                status: (() => {
                    if (contract.contractServiceProviders?.length > 0) {
                        const hasIncompleteProviders = contract.contractServiceProviders.some(provider => {
                            const hasBasicInfo = provider.name?.firstName &&
                                provider.name?.lastName &&
                                provider.contractedServiceProviderType?.id &&
                                provider.sites?.sites?.length > 0;

                            return !hasBasicInfo;
                        });

                        if (hasIncompleteProviders) {
                            return "not_completed";
                        } else {
                            return "completed";
                        }
                    } else {
                        return "completed";
                    }
                })()
            },
            {
                title: "Contractor Business Entity",
                status: (() => {
                    if (!contract.contractorBusinessEntity || contract.contractorBusinessEntity.id === null) {
                        return "inactive";
                    }

                    if (contract.contractorBusinessEntity.completionStatus === "NOT_COMPLETED") {
                        return "not_completed";
                    }
                    if (contract.contractorBusinessEntity.completionStatus === "PARTIAL") {
                        return "partial";
                    }
                    if (contract.contractorBusinessEntity.completionStatus === "COMPLETED") {
                        return "completed";
                    }

                    return "inactive";
                })()
            },
            {
                title: "Contracted Services Specification",
                status: (() => {
                    if (!contract.contractedServices || contract.contractedServices.length === 0) {
                        return "inactive";
                    }

                    const hasNotCompletedServices = contract.contractedServices.some(service =>
                        service.completionStatus === "NOT_COMPLETED"
                    );
                    const hasPartialServices = contract.contractedServices.some(service =>
                        service.completionStatus === "PARTIAL"
                    );
                    const hasCompletedServices = contract.contractedServices.some(service =>
                        service.completionStatus === "COMPLETED"
                    );

                    if (hasNotCompletedServices) {
                        return "not_completed";
                    }
                    if (hasPartialServices) {
                        return "partial";
                    }
                    if (hasCompletedServices) {
                        return "completed";
                    }

                    return "inactive";
                })()
            },
            {
                title: "Timesheet Submission Terms",
                status: (() => {
                    const timesheetTermsArray = Array.isArray(contract.timesheetSubmissionTerms) ?
                        contract.timesheetSubmissionTerms :
                        (contract.timesheetSubmissionTerms ? [contract.timesheetSubmissionTerms] : []);

                    if (timesheetTermsArray.length === 0) {
                        return "inactive";
                    }

                    const hasNotCompletedTerms = timesheetTermsArray.some(terms =>
                        terms.completionStatus === "NOT_COMPLETED"
                    );
                    const hasPartialTerms = timesheetTermsArray.some(terms =>
                        terms.completionStatus === "PARTIAL"
                    );
                    const hasCompletedTerms = timesheetTermsArray.some(terms =>
                        terms.completionStatus === "COMPLETED"
                    );

                    if (hasNotCompletedTerms) {
                        return "not_completed";
                    }
                    if (hasPartialTerms) {
                        return "partial";
                    }
                    if (hasCompletedTerms) {
                        return "completed";
                    }

                    return "inactive";
                })()
            },
            {
                title: "Payment & Compensation",
                status: (() => {
                    if (!contract.paymentAndCompensation) {
                        return "inactive";
                    }
                    if (contract.paymentAndCompensation.completionStatus === "NOT_COMPLETED") {
                        return "not_completed";
                    }
                    if (contract.paymentAndCompensation.completionStatus === "PARTIAL") {
                        return "partial";
                    }
                    if (contract.paymentAndCompensation.completionStatus === "COMPLETED") {
                        return "completed";
                    }

                    return "inactive";
                })()
            }
        ];

        const workflowSections = [
            {
                status: getWorkflowSectionStatus("Activity Logging", workflowData)
            },
            {
                status: getWorkflowSectionStatus("Timesheet Creation & Submission", workflowData)
            },
            {
                status: getWorkflowSectionStatus("Timesheet Review & Approval", workflowData)
            },
            {
                status: getWorkflowSectionStatus("Invoice Review & Approval", workflowData)
            }
        ];

        // Apply the same logic as ContractSummary
        const sectionsWithWarnings = contractSections.filter(section => section.status === 'not_completed');
        const sectionsWithPartials = contractSections.filter(section => section.status === 'partial');
        const allContractSectionsCompleted = contractSections.every(section => section.status === 'completed');

        const workflowWithWarnings = workflowSections.filter(section => section.status === 'not_completed');
        const workflowWithPartials = workflowSections.filter(section => section.status === 'partial');
        const incompleteWorkflows = workflowSections.filter(section => section.status === 'inactive' || section.status === 'not_completed');
        const allWorkflowsCompleted = workflowSections.every(section => section.status === 'completed');

        const contractDataComplete = allContractSectionsCompleted && sectionsWithWarnings.length === 0 && sectionsWithPartials.length === 0;
        const workflowDataComplete = allWorkflowsCompleted && workflowWithWarnings.length === 0 && workflowWithPartials.length === 0 && incompleteWorkflows.length === 0;

        if (sectionsWithWarnings.length > 0 || workflowWithWarnings.length > 0) {
            return fileIcon;
        }

        if ((contractDataComplete && !workflowDataComplete) ||
            (!contractDataComplete && workflowDataComplete) ||
            sectionsWithPartials.length > 0 ||
            workflowWithPartials.length > 0 ||
            incompleteWorkflows.length > 0) {
            return yellowWarning;
        }

        if (contractDataComplete && workflowDataComplete) {
            return CompletedIcon;
        }

        return fileIcon;
    };

    const { alerts, alertIcons } = getAlertsAndIcons();

    const handleDelete = async () => {
        if (!contractId) {
            console.error('No contract ID provided');
            return;
        }

        // if (!deleteReason) {
        //     console.error('Delete reason is required');
        //     return;
        // }

        setIsDeleting(true);
        console.log('Deleting contract with ID:', contractId);
        console.log('Delete reason:', deleteReason);
        console.log('Delete option:', deleteOption);

        try {
            const apiUrl = `contract-managment-service/contracts/${contractId}?notes=${deleteReason}`;

            console.log('Making DELETE request to:', apiUrl);

            const response = await DELETE(apiUrl);

            console.log('DELETE response status:', response.status);

            SuccessToaster('Contract deleted successfully');
            onClose();
        } catch (error) {
            console.error('Error deleting contract:', error);
            alert('Failed to delete contract. Please try again.');
        } finally {
            setIsDeleting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Dialog isOpen={isOpen} className={`${styles.dialogStyle} ${styles.dialogPaddingBottom}`}>
            <div className={`${Classes.DIALOG_BODY} ${styles.extensionDialogBackground}`}>
                <div className={styles.spaceBetween}>
                    <p className={styles.extensionStyle}>Delete This Contract</p>
                    <Icon icon="cross" size={20} intent={Intent.DANGER} className={styles.crossStyle} onClick={onClose} />
                </div>
                <div className={styles.extensionBorder}></div>
                <div style={{ paddingTop: '10px' }}>
                    <ContractHeader
                        contractTitle={
                            apiContractData?.contract?.contractName?.contractName ||
                            contractData?.contractName?.contractName ||
                            contractData?.contractName ||
                            "Contract Name"
                        }
                        isDraft={
                            apiContractData?.contract?.contractStatus === 'DRAFT' ||
                            contractData?.contractStatus === 'DRAFT'
                        }
                        contractSubtitle={
                            apiContractData?.contract?.contractType
                                ? apiContractData.contract.contractType === "MULTIPLE"
                                    ? "Multi Provider Contract"
                                    : apiContractData.contract.contractType === "BATCH"
                                        ? "Batch Provider Contract"
                                        : apiContractData.contract.contractType === "INDIVIDUAL"
                                            ? "Solo Provider Contract"
                                            : "Multiple Provider Contract"
                                : contractData?.contractType
                                    ? contractData.contractType === "MULTIPLE"
                                        ? "Multi Provider Contract"
                                        : contractData.contractType === "BATCH"
                                            ? "Batch Provider Contract"
                                            : contractData.contractType === "INDIVIDUAL"
                                                ? "Solo Provider Contract"
                                                : "Multiple Provider Contract"
                                    : "Contract Type"
                        }
                        contractId={
                            apiContractData?.contract?.contractDetail?.contractId?.id ||
                            contractData?.contractDetail?.contractId?.id ||
                            contractData?.contractId ||
                            contractData?.id ||
                            contractId ||
                            "Contract ID"
                        }
                        alerts={alerts}
                        alertIcons={alertIcons}
                        alertIcon={getAlertIcon()}
                        lastUpdated={
                            apiContractData?.contract?.lastModifiedDate
                                ? format(new Date(apiContractData.contract.lastModifiedDate), "MMM dd, yyyy")
                                : contractData?.lastModifiedDate
                                    ? format(new Date(contractData.lastModifiedDate), "MMM dd, yyyy")
                                    : "N/A"
                        }
                        lastUpdatedBy={workflowData?.lastModifiedBy?.name?.name}
                    />
                </div>

                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Reasons For Deleting This Contract</h3>
                    <CKEditor
                        editor={ClassicEditor}
                        data={deleteReason}
                        onChange={(event, editor) => {
                            const data = editor.getData();
                            setDeleteReason(data);
                        }}
                        config={{
                            placeholder: "Enter your Reason here",
                            toolbar: {
                                shouldNotGroupWhenFull: true,
                                sticky: true,
                                items: [
                                    'undo', 'redo',
                                    '|',
                                    'heading',
                                    '|',
                                    'fontfamily', 'fontsize', 'fontColor', 'fontBackgroundColor',
                                    '|',
                                    'bold', 'italic', 'strikethrough', 'subscript', 'superscript', 'code',
                                    '|',
                                    'bulletedList', 'numberedList', 'todoList', 'outdent', 'indent'
                                ],
                            },
                            autoGrow: false,
                        }}
                        onReady={(editor) => {
                            const editorElement = editor.editing.view.document.getRoot();
                            editor.editing.view.change(writer => {
                                writer.setStyle(
                                    'min-height',
                                    '150px',
                                    editorElement
                                );
                            });
                        }}

                    />
                </div>

                <div className={styles.section}>
                    <div className={styles.radioGroup}>
                        <div className={styles.radioItem}>
                            <input
                                type="radio"
                                id="deleteWithResend"
                                name="deleteOption"
                                value="deleteWithResend"
                                checked={deleteOption === 'deleteWithResend'}
                                onChange={() => setDeleteOption('deleteWithResend')}
                                className={styles.radio}
                                style={{ display: 'none' }}
                            />
                            <div
                                className={`${styles.radioButton} ${deleteOption === 'deleteWithResend' ? styles.selected : ''}`}
                                onClick={() => setDeleteOption('deleteWithResend')}
                            />
                            <label
                                htmlFor="deleteWithResend"
                                className={styles.radioLabel}
                                onClick={() => setDeleteOption('deleteWithResend')}
                            >
                                Delete Draft With Request To Resend eCATS Record
                            </label>
                        </div>

                        <div className={styles.radioItem}>
                            <input
                                type="radio"
                                id="deleteDraft"
                                name="deleteOption"
                                value="deleteDraft"
                                checked={deleteOption === 'deleteDraft'}
                                onChange={() => setDeleteOption('deleteDraft')}
                                className={styles.radio}
                                style={{ display: 'none' }}
                            />
                            <div
                                className={`${styles.radioButton} ${deleteOption === 'deleteDraft' ? styles.selected : ''}`}
                                onClick={() => setDeleteOption('deleteDraft')}
                            />
                            <label
                                htmlFor="deleteDraft"
                                className={styles.radioLabel}
                                onClick={() => setDeleteOption('deleteDraft')}
                            >
                                Delete Draft
                            </label>
                        </div>
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.actionButtons}>
                        <button
                            onClick={onClose}
                            className={styles.cancelButton}>
                            CANCEL
                        </button>
                        <div
                            onClick={handleDelete}
                            disabled={isDeleting}
                            className={styles.resendButton}>
                            {isDeleting ? 'DELETING...' : 'DELETE DRAFT'}
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default DeleteContractModal;